import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useCompanyProfit, useCompany } from "hooks/api/options";
import { useProductCenter } from "hooks/api";
import { Form } from "@unform/web";
import { SubmitHandler, FormHandles } from "@unform/core";
import * as Yup from "yup";
import { Select } from "components/shared/Form";
import { Trash2, X } from "lucide-react";
import { notify } from "services";
import { useTranslation } from "hooks";
import { translations } from "./translations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/ui/ButtonGs";

interface IParams {
  id: string; // Product ID
}

const CenterProfit: React.FC = () => {
  const { id } = useParams<IParams>();
  const subFormRef = useRef<FormHandles>(null);
  const subFormProfitRef = useRef<FormHandles>(null);
  const queryClient = useQueryClient();
  const { getListCompanyProfit } = useCompanyProfit();
  const { getListCompanyOldSelect } = useCompany();
  const { getTranslation } = useTranslation(translations);
  const {
    associateProfitCenter,
    createProductCenter,
    getListProductCenters,
    deleteProfitCenter,
    deleteProductCenter,
  } = useProductCenter();
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [centerToDelete, setCenterToDelete] = useState<number | null>(null);

  const { data: optionCompanyProfit, isLoading: loadingOptionCompanyProfit } =
    useQuery("getListCompanyProfit", () => getListCompanyProfit({ all: true }));
  const { data: optionCompany, isLoading: loadingOptionCompany } = useQuery(
    "getListCompanyOldSelect",
    () => getListCompanyOldSelect({ all: true })
  );

  const { data: productCenters, refetch: refetchProductCenters } = useQuery(
    "getListProductCenters",
    () => getListProductCenters({ product_id: Number(id), all: true })
  );

  const mutationAddCenter = useMutation(
    (newCenter: { company_id: number; product_id: number }) =>
      createProductCenter({
        company_id: newCenter.company_id,
        product_id: Number(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("getListCompanyProfit");
        queryClient.invalidateQueries("getListCompanyOldSelect");
        refetchProductCenters();
      },
    }
  );

  const mutationAddProfit = useMutation(
    (data: { product_center_id: number; company_profit_id: string }) =>
      associateProfitCenter(data.product_center_id, {
        product_center_id: data.product_center_id,
        company_profit_id: Number(data.company_profit_id),
        product_id: Number(id),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("getListCompanyProfit");
        queryClient.invalidateQueries("getListCompanyOldSelect");
        refetchProductCenters();
      },
    }
  );

  const mutationDeleteProfit = useMutation(
    (profit_id: number) => deleteProfitCenter(profit_id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("getListCompanyProfit");
        queryClient.invalidateQueries("getListCompanyOldSelect");
        refetchProductCenters();
      },
    }
  );

  const handleSubmit: SubmitHandler = async (values) => {
    try {
      subFormRef.current?.setErrors({});
      const schema = Yup.object().shape({
        company_id: Yup.number().required("Centro é obrigatório"),
      });
      await schema.validate(values, { abortEarly: false });

      // Regra desconsiderada no momento - Evelyn
      //   if (productCenters && productCenters.length >= 3) {
      //     notify("error", getTranslation("centerMax"));
      //     return;
      //   }

      if (
        productCenters &&
        productCenters.some((item) => item.company_id === values.company_id)
      ) {
        notify("error", getTranslation("centerExist"));
        return;
      }

      mutationAddCenter.mutate({
        company_id: values.company_id,
        product_id: Number(id),
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        error.inner.forEach((err) => {
          if (err.path) validationErrors[err.path] = err.message;
        });
        subFormRef.current?.setErrors(validationErrors);
      }
    }
  };

  const handleAddProfit = (center_id: number, profit_center: string) => {
    const center = productCenters?.find((item) => item.id === center_id);

    if (!profit_center) {
      notify("error", getTranslation("nenhumCentroSelecionado"));
      return;
    }

    if (center && center.profits && center.profits.length >= 3) {
      notify("error", getTranslation("profitMax"));
      return;
    }

    if (
      center &&
      center.profits &&
      center.profits.some(
        (profit) => profit.company_profit_id === Number(profit_center)
      )
    ) {
      notify("error", getTranslation("profitExist"));
      return;
    }

    mutationAddProfit.mutate({
      product_center_id: center_id,
      company_profit_id: profit_center,
    });
  };

  const handleDeleteProfit = (profit_id: number) => {
    mutationDeleteProfit.mutate(profit_id);
  };

  const mutationDeleteCenter = useMutation(
    (center_id: number) => deleteProductCenter(center_id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("getListProductCenters");
        refetchProductCenters();
        notify("success", getTranslation("centerDeleted"));
        setDeleteModalOpen(false);
      },
      onError: () => {
        notify("error", getTranslation("deleteError"));
      },
    }
  );

  const handleOpenDeleteModal = (center_id: number) => {
    setCenterToDelete(center_id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (centerToDelete !== null) {
      mutationDeleteCenter.mutate(centerToDelete);
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h2 className="text-2xl font-bold mb-4">
        {getTranslation("gerenciarCentrosDeLucro")}
      </h2>
      <Form
        ref={subFormRef}
        onSubmit={handleSubmit}
        className="space-y-4"
        placeholder=""
      >
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label
              htmlFor="company_id"
              className="text-sm font-medium mb-1 block"
            >
              {getTranslation("selecioneUmCentro")}
            </label>
            <Select
              name="company_id"
              isDisabled={loadingOptionCompany}
              isLoading={loadingOptionCompany}
              placeholder={getTranslation("selecione")}
              options={optionCompany || []}
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
            disabled={mutationAddCenter.isLoading}
          >
            {mutationAddCenter.isLoading
              ? getTranslation("adicionando")
              : getTranslation("adicionar")}
          </button>
        </div>
      </Form>
      {productCenters && productCenters.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2">
            {getTranslation("centros")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {productCenters.map((productCenter) => (
              <div
                key={productCenter.id}
                className="p-4 bg-white shadow rounded flex flex-col items-start"
              >
                <div className="flex items-center justify-between w-full">
                  <span className="text-base font-bold">
                    {productCenter.company.code || ""} -{" "}
                    {productCenter.company.trade_name || ""}
                  </span>
                  <button
                    onClick={() => handleOpenDeleteModal(productCenter.id)}
                    className="p-2 text-red-500 hover:text-red-600"
                    title={getTranslation("deleteCenter")}
                  >
                    <X size={18} />
                  </button>
                </div>
                <hr className="w-full my-2 border-gray-200" />
                <div className="mt-2 w-full">
                  {productCenter.profits?.map((profit) => (
                    <div
                      key={`profit${profit.id}`}
                      className="flex items-center justify-between mb-2"
                    >
                      <span className="text-sm">
                        <b>{getTranslation("centroAssociado")}:</b>{" "}
                        {profit.companyProfit.code || ""} (
                        {profit.companyProfit.businessLine.activity_division ||
                          ""}{" "}
                        - {profit.companyProfit.businessLine.description || ""})
                      </span>
                      <button
                        onClick={() => handleDeleteProfit(profit.id)}
                        className="p-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 flex items-center"
                        disabled={mutationDeleteProfit.isLoading}
                        title="Remover"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                  <Form
                    placeholder={""}
                    ref={subFormProfitRef}
                    onSubmit={(data) =>
                      handleAddProfit(productCenter.id, data.profit_center)
                    }
                    className="flex items-center gap-4"
                  >
                    <Select
                      name="profit_center"
                      isDisabled={loadingOptionCompanyProfit}
                      isLoading={loadingOptionCompanyProfit}
                      placeholder={getTranslation("adicionarCentro")}
                      options={optionCompanyProfit || []}
                      className="flex-1"
                      containerStyle={{ margin: "0px" }}
                    />
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                    >
                      {getTranslation("adicionar")}
                    </button>
                  </Form>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog
        open={isDeleteModalOpen}
        onOpenChange={() => setDeleteModalOpen(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-GilroyBold text-lg tracking-normal">
              {getTranslation("confirmDeleteCenter")}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>
            <article className="mb-4">
              {getTranslation("deleteCenterWarning")}
            </article>
          </DialogDescription>
          <DialogFooter>
            <Button.Root
              size="sm"
              variant="secondary"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancelar
            </Button.Root>
            <Button.Root
              size="sm"
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={mutationDeleteCenter.isLoading}
            >
              {mutationDeleteCenter.isLoading
                ? getTranslation("deleting")
                : getTranslation("deleteCenter")}
            </Button.Root>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CenterProfit;
